<template>
  <o-dropdown
    ref="dropdown"
    :anchor="anchor"
    placement="right-start"
    padding=""
    default-class="z-10 origin-top-right min-w-52 bg-gray-800/90 backdrop-filter backdrop-blur-md rounded-lg text-white shadow-lg"
    :clickaway="false"
    :arrow="false"
    :fixed="false"
  >
    <template #content>
      <div v-if="hover" class="text-sm w-60 text-left overflow-x-hidden max-h-[min(30rem,var(--reka-popover-content-available-height,30rem))] overflow-y-auto overscroll-contain">
        <div class="p-3 bg-gray-900/50 rounded-t-lg flex justify-between">
          <div>
            <p class="font-semibold text-base">
              {{ hover.label }}
            </p>
            <p class="opacity-80">
              {{ hover.reservationService.employee.displayName }}
            </p>
          </div>
        </div>
        <div class="p-3">
          <div v-if="!privacyMode" class="pb-3">
            <p class="opacity-50">
              {{ $t('general.colon', { label: $t('general.price') }) }}
            </p>
            <p class="font-medium">
              {{ hover.reservation.priceLabel }}
            </p>
          </div>
          <div class="pb-3">
            <p class="opacity-50">
              {{ $t('general.colon', { label: $t('general.duration') }) }}
            </p>
            <p class="font-medium">
              {{ $t('datetime.increments.mins', { mins: hover.reservationService.duration }, Number(hover.reservationService.duration)) }}
            </p>
          </div>
          <div v-if="isCourse" class="pb-3">
            <p class="opacity-50">
              Course:
            </p>
            <ul class="font-medium list-disc list-outside ml-5">
              <template v-for="courseRunReservation in reservation.courseRunReservations" :key="courseRunReservation.idCourseRunReservation">
                <li>
                  <p class="text-white cursor-pointer">
                    {{ courseRunReservation.courseRun.course.name }} - {{ courseRunReservation.courseRun.information }}
                  </p>
                </li>
              </template>
            </ul>
          </div>
          <div v-if="hasPass" class="pb-3">
            <p class="opacity-50">
              {{ $t('general.colon', { label: $t('general.pass') }) }}
            </p>
            <p class="font-medium">
              {{ pass.pass.name }}
              <span class="active text-xs font-normal">({{ pass.infoLabel }})</span>
            </p>
          </div>
          <div v-if="hasServices" class="pb-3">
            <p class="opacity-50">
              {{ $t('components.schedule.elements.preview.otherServices') }}
            </p>
            <ul class="font-medium list-disc list-outside ml-5">
              <template v-for="service in services" :key="'link-'+service.hash">
                <li>
                  <p class="text-white cursor-pointer">
                    {{ service.label }}
                  </p>
                </li>
              </template>
            </ul>
          </div>
          <div v-if="hasResources" class="pb-3">
            <p class="opacity-50">
              {{ $t('general.colon', { label: $t('general.resources') }) }}
            </p>
            <ul class="font-medium list-disc list-outside ml-5">
              <template v-for="resource in resources" :key="'link-'+resource.hash">
                <li>
                  <p class="text-white cursor-pointer">
                    {{ resource.resourceLabel }}
                  </p>
                </li>
              </template>
            </ul>
          </div>
          <div v-if="!privacyMode && hasNotes" class="pb-3">
            <p class="opacity-50">
              {{ $t('general.colon', { label: $t('general.notes') }) }}
            </p>
            <p class="font-medium">
              {{ hover.reservation.notes }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </o-dropdown>
</template>

<script setup lang="ts">
import {
  ReservationBlock,
  ReservationResourceBlock,
  ReservationCustomerResourceBlock,
} from '../provider/block'

import { useUIStore } from '~/stores/useUIStore'

const uiStore = useUIStore()

const modifier = inject('modifier') as any
const dropdown = ref<any>()

const privacyMode = computed(() => uiStore.privacyMode)
const hover = computed(() => modifier.active.hover)
const item = computed(() => modifier.active.item)
const visible = computed(() => hover.value && !item.value)
const reservation = computed(() => hover.value?.reservation)

const anchor = computed(() => {
  if (hover.value?.hash) {
    return document.getElementById(hover.value.hash)
  }

  return true
})

const services = computed(() => {
  return hover.value.relatedBlocks.filter((block: any) => {
    return block.constructor === ReservationBlock && !block.isTimingFinishing && block.reservationService.idService !== hover.value.reservationService.idService
  })
})

const resources = computed(() => {
  return hover.value.relatedBlocks.filter((block: any) => (block instanceof ReservationResourceBlock || block instanceof ReservationCustomerResourceBlock))
})

const hasPass = computed(() => !!pass.value)
const pass = computed(() => null) // pass not defined in original, keeping for hasPass
const hasServices = computed(() => services.value.length)
const hasResources = computed(() => resources.value.length)
const hasNotes = computed(() => reservation.value.notes.length)
const isResourceBlock = computed(() => hover.value.isResourceBlock)
const isCourse = computed(() => reservation.value.courseRunReservations?.length > 0)

watch(visible, (val) => {
  if (val) {
    dropdown.value.open()
  } else {
    dropdown.value.close()
  }
})
</script>
